// Gastenpagina: header met tabbladen en wieg-icoon, de hero, en de routes.
// #/ is de lijst (met ?cat/toon/sort), #/kaartje en #/fotos de galerij, #/betalen/<token> het betaalscherm.
import { openCart } from './cart-dialogs.ts';
import { $, closeDialog, initDialog, toast } from './dom.ts';
import { closeLightbox, initLightbox, renderCards, renderPhotos } from './gallery.ts';
import { icon } from './icons.ts';
import { initListControls, listHref, renderListError, renderListView, setQueryFromRoute } from './list-view.ts';
import { showPay } from './pay.ts';
import { currentCart, currentList, droppedMessage, onChange, reloadList } from './store.ts';
import { availableView, isRouteHash, initTabKeys, panelId, parseRoute, renderTabs, type Available, type View } from './tabs.ts';

const REFRESH_MS = 60_000;
const DEFAULT_TITLE = 'Onze geboortelijst';
const PANELS = ['listView', 'cardView', 'photoView', 'payView'];

let lastCount = currentCart().length;

function available(): Available {
  const list = currentList();
  return { cards: (list?.cards.length ?? 0) > 0, photos: (list?.photos.length ?? 0) > 0 };
}

/** De zichtbare view volgens de hash; null op het betaalscherm. */
function currentView(): View | null {
  const r = parseRoute(location.hash);
  return r.kind === 'pay' ? null : availableView(r.view, available());
}

function renderHero(): void {
  const s = currentList()?.settings;
  $('title').textContent = s?.title || DEFAULT_TITLE;
  $('subtitle').textContent = s?.subtitle ?? '';
  $('subtitle').hidden = !s?.subtitle;
  $('intro').textContent = s?.intro ?? '';
  $('intro').hidden = !s?.intro;
}

function renderCartIcon(): void {
  const n = currentCart().length;
  const count = $('cartCount');
  count.textContent = n > 9 ? '9+' : String(n);
  count.hidden = n === 0;
  $('cartOpen').setAttribute('aria-label', n === 0 ? 'Je wiegje is leeg' : `Je wiegje: ${n === 1 ? '1 cadeau' : `${n} cadeaus`}`);
  if (n > lastCount) {
    count.classList.remove('bump');
    void count.offsetWidth; // herstart de animatie
    count.classList.add('bump');
  }
  lastCount = n;
}

function showPanels(view: View | null): void {
  const visible = view === null ? 'payView' : panelId(view);
  for (const id of PANELS) $(id).hidden = id !== visible;
  $('hero').hidden = view === null;
  $('hero').classList.toggle('compact', view !== 'lijst');
}

function render(): void {
  const list = currentList();
  const view = currentView();
  renderHero();
  renderTabs($('tabs'), view, available(), listHref());
  renderCartIcon();
  showPanels(view);
  if (view === 'lijst') renderListView(list);
  else if (view === 'kaartje' && list) renderCards(list.cards);
  else if (view === 'fotos' && list) renderPhotos(list.photos);
}

async function refresh(): Promise<void> {
  try {
    const dropped = await reloadList();
    if (dropped.length) toast(droppedMessage(dropped));
  } catch {
    if (!currentList()) renderListError(); // anders blijft de vorige lijst staan
  }
}

function route(): void {
  if (!isRouteHash(location.hash)) return;
  const r = parseRoute(location.hash);
  if (r.kind === 'view' && r.view === 'lijst') setQueryFromRoute(r.params);
  closeDialog();
  closeLightbox();
  render();
  window.scrollTo(0, 0);
  if (r.kind === 'pay') void showPay(r.token);
}

function initSkipLink(): void {
  const skip = document.querySelector('a.skip');
  const main = document.getElementById('main');
  if (!skip || !main) return;
  // Geen hashwijziging: de router blijft op de huidige route (ook #/betalen/<token>).
  skip.addEventListener('click', (e) => {
    e.preventDefault();
    main.focus();
  });
}

function initGansje(): void {
  const gansje = document.getElementById('gansje');
  if (!(gansje instanceof HTMLImageElement)) return;
  const drop = (): void => gansje.remove();
  gansje.addEventListener('error', drop);
  if (gansje.complete && gansje.naturalWidth === 0) drop();
}

function boot(): void {
  initGansje();
  initSkipLink();
  initDialog();
  initLightbox();
  initTabKeys($('tabs'));
  initListControls(render);
  $('cartIcon').replaceWith(icon('crib'));
  $('brand').prepend(icon('heart'));
  $('cartOpen').addEventListener('click', () => openCart());
  onChange(render);
  window.addEventListener('hashchange', route);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void refresh();
  });
  setInterval(() => {
    if (document.visibilityState === 'visible') void refresh();
  }, REFRESH_MS);

  route();
  void refresh();
}

boot();
